import './style.scss'
import React, { useEffect, useState } from 'react'
import checkWobbleIcon from 'src/assets/v2/img/check_wobble_icon.svg'
import alertTriangleIcon from 'src/assets/v2/img/alert_triangle_icon.svg'

export type NotificationVariant =
    | 'success'
    | 'warning'
    | 'error'

export type NotificationPosition =
    | 'top-right'
    | 'top-center'
    | 'top-left'
    | 'bottom-right'
    | 'bottom-center'
    | 'bottom-left'
    | 'inline'

export interface NotificationProps {
    message: React.ReactNode
    actionLabel?: string
    onAction?: () => void
    onClose?: () => void
    icon?: React.ReactNode
    duration?: number
    visible?: boolean
    position?: NotificationPosition
    variant?: NotificationVariant
    className?: string
}

const DEFAULT_ICON_BY_VARIANT: Record<NotificationVariant, string> = {
    success: checkWobbleIcon,
    warning: alertTriangleIcon,
    error: alertTriangleIcon,
}

export const Notification: React.FC<NotificationProps> = ({
                                                              message,
                                                              actionLabel = 'Dismiss',
                                                              onAction,
                                                              onClose,
                                                              icon,
                                                              duration = 4000,
                                                              visible = true,
                                                              position = 'top-right',
                                                              variant = 'success',
                                                              className = '',
                                                          }) => {
    const [show, setShow] = useState(visible)
    useEffect(() => {
        setShow(visible)
    }, [visible])
    useEffect(() => {
        if (!show || duration === 0) return
        const t = setTimeout(() => {
            setShow(false)
            onClose?.()
        }, duration)
        return () => clearTimeout(t)
    }, [show, duration, onClose])
    const handleClose = () => {
        setShow(false)
        onClose?.()
    }
    const handleAction = () => {
        setShow(false)
        onAction?.()
        onClose?.()
    }
    if (!show) return null
    return (
        <div
            className={`notification notification--${position} notification--${variant} ${className}`}
            role="alert"
            aria-live="polite"
        >
            <span className="notification__icon">
                {icon ?? <img src={DEFAULT_ICON_BY_VARIANT[variant]} alt="" />}
            </span>
            <div className="notification__body">
                <span className="notification__message">{message}</span>
                {actionLabel && (
                    <button
                        type="button"
                        className="notification__action"
                        onClick={handleAction}
                    >
                        {actionLabel}
                    </button>
                )}
            </div>
            <button
                type="button"
                className="notification__close"
                aria-label="Close notification"
                onClick={handleClose}
            >
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 4L4 12M4 4L12 12" stroke="#667085" strokeWidth="1.5" strokeLinecap="round"/>
                </svg>
            </button>
        </div>
    )
}

export default Notification